import {faCog, faFilter, faInfoCircle, faRotate} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import moment from 'moment'
import React, {FC, Fragment, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  Button,
  OverlayTrigger,
  Overlay,
  Popover,
  Spinner,
  Tooltip,
  Form,
  Modal,
  ProgressBar,
  Dropdown,
} from 'react-bootstrap'
import DataTable from 'react-data-table-component'
import {Link, useSearchParams, useNavigate} from 'react-router-dom'
import {
  getOrdersListAsync,
  orderResyncAsync,
  shippingResyncAsync,
  onholdOrderResyncAsync,
  getOrderPackageByOrderIdAsync,
  saveOrderPackageAsync,
  downloadShippingLabelsAsync,
  requestNewShippingLabelAsync,
  downloadOrderInvoiceAsync,
} from 'src/redux/actions/orderActions'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import StatusButton from './StatusButton'
import {toast} from 'react-toastify'
import AppDatePickerRange from './components/AppDatePickerRange'
import useAppToast from 'src/hooks/useAppToast'
import ShipmentStatusButton from './components/ShipmentStatusButton'
import {getShipmentSyncSummary} from './shipmentStatusUtils'
import { getAllPlatformTypesAsync } from 'src/redux/actions/platformActions'
import { formatDateLocal, formatTimeZone } from 'src/utils/TimeZoneDateFormat'
import TableLoadingShell from 'src/utils/TableLoadingShell'
import { useCustomerAccess } from 'src/hooks/useCustomerAccess'
import { shouldShowAction, getActionButtonProps } from 'src/utils/customerAccessUtils'
import { usePermission } from 'src/hooks/usePermission'
import ManagePackagesModal from './components/ManagePackagesModal'
import ManageShipmentsModal from './components/ManageShipmentsModal'
import OrderRowActionsCell from './components/OrderRowActionsCell'
import OrderPackageModal, {PackageFormState} from './components/OrderPackageModal'
import { hasOmsOrderAccess } from './orderAccessUtils'
import {
  downloadShippingLabelsIndividually,
  mergeShippingLabelUrls,
} from './shippingLabelUtils'
import {
  filterOrdersForAmazonVendorDocumentDownload,
  filterOrdersForDocumentDownload,
  filterOrdersForDocumentDownloadByType,
  isLegacyShippingLabelPlatform,
  resolveShowAmazonVendorLabelDocumentDownloadActions,
  resolveShowLegacyShippingLabelActions,
  resolveShowOrderDocumentDownloadActions,
  runOrderDocumentDownload,
} from './orderDocumentDownloadUtils'
import type {OrderPackingSlipDownloadType} from './orderPackingSlipUtils'
import { AppUrls } from 'src/app/routing/AppUrls'
import UploadCsvOrdersModal from '../create-product/UploadCsvOrdersModal'
import {
  parseDashboardPendingOrderActionFromSearchParams,
  resolveDashboardPendingActionFilters,
  buildDashboardDeepLinkColumnFilters,
} from './utils/resolveDashboardPendingActionFilters'
import type {DashboardPendingOrderAction} from '../dashboard/utils/pendingActionNavigation'

// shallow compare two plain objects (string/boolean values)
const isShallowEqual = (a: Record<string, any> = {}, b: Record<string, any> = {}) => {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  for (const key of aKeys) {
    if (a[key] !== b[key]) return false
  }
  return true
}

const TableCheckbox = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({style, ...rest}, ref) => (
    <input
      ref={ref}
      type='checkbox'
      {...rest}
      style={{
        cursor: 'pointer',
        margin: 0,
        width: '16px',
        height: '16px',
        ...style,
      }}
    />
  )
)
TableCheckbox.displayName = 'TableCheckbox'

const ColProductName: FC<{
  rowData: any
  omsPlatformId?: any
}> = ({rowData, omsPlatformId}) => {
  const {hasAccess, effectiveOrderId} = hasOmsOrderAccess(rowData, omsPlatformId)
  const redirectUrl = hasAccess
    ? AppUrls.editOmsOrder(effectiveOrderId!)
    : AppUrls.salesOrderDetails(rowData?.id)

  return (
    <div className='w-100'>
      <div>
        <p className=' d-inline-block text-truncate mb-0 me-2 ' >
          <Link
            className='fw-bold  fs-5 link-underline-info py-0 text-success text-decoration-underline mb-0'
            to={redirectUrl}
          >
            {rowData?.api_code}
          </Link>
        </p>
      </div>
    </div>                                  
  )
}
const CommonColumn: FC<{ rowData: string }> = ({ rowData }) => {
  return (
    <div className="row">
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{rowData}</Tooltip>}
      >
        <p className="d-inline-block" style={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
          {rowData}
        </p>
      </OverlayTrigger>
    </div>
  );
};

interface StatusFilters {
  sync_status?: string;
  shipment_status?: string;
  accounting_sync_status?: string;
  shipping_sync_status?: string;
  cancel_status?: boolean | string;
}

const areShallowEqual = (a: Record<string, any> = {}, b: Record<string, any> = {}) => {
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  for (const key of keys) {
    if (a?.[key] !== b?.[key]) return false;
  }
  return true;
};

const SOURCE_ORDER_CHANNEL_TYPES = new Set(['primary', 'accounting', 'shipping', 'erp', 'oms'])
// platform.name values that support shipping-label bulk actions (per API)
const SHIPPING_LABEL_PLATFORM_NAMES = new Set(['TEMU', 'AMAZONVENDOR', 'AMAZONSELLER'])
const REQUEST_NEW_SHIPPING_LABEL_PLATFORM_NAMES = new Set(['TEMU'])

const isShippingLabelPlatform = (platformName: string | null | undefined) => {
  const normalized = platformName?.toUpperCase()?.trim()
  return Boolean(normalized && SHIPPING_LABEL_PLATFORM_NAMES.has(normalized))
}

const canRequestNewShippingLabel = (platformName: string | null | undefined) => {
  const normalized = platformName?.toUpperCase()?.trim()
  return Boolean(normalized && REQUEST_NEW_SHIPPING_LABEL_PLATFORM_NAMES.has(normalized))
}

const hasConnectedShippingLabelPlatform = (platforms: any[] = []) =>
  platforms.some(
    (platform: any) => isShippingLabelPlatform(platform?.name) && platform?.isConnected === true
  )

const hasConnectedRequestNewShippingLabelPlatform = (platforms: any[] = []) =>
  platforms.some(
    (platform: any) => canRequestNewShippingLabel(platform?.name) && platform?.isConnected === true
  )
const PLATFORM_CHANNEL_TYPE_BY_FLAG: Array<{flag: string; channelType: string}> = [
  {flag: 'isAccountingStore', channelType: 'accounting'},
  {flag: 'isShippingStore', channelType: 'shipping'},
  {flag: 'isErpStore', channelType: 'erp'},
  {flag: 'isOmsStore', channelType: 'oms'},
  {flag: 'isMainStore', channelType: 'primary'},
]

const formatOrderColumnWord = (word: string) => {
  if (!word) return word
  const lower = word.toLowerCase()
  if (lower === 'erp') return 'ERP'
  if (lower === 'oms') return 'OMS'
  if (word === '#') return '#'
  if (/^[A-Z]{2,}$/.test(word)) return word
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
}

const formatOrderColumnDisplayName = (displayName: string) => {
  if (!displayName?.trim()) return displayName
  if (displayName === 'Primary Order Status') return 'Source Order Status'
  return displayName
    .trim()
    .split(/\s+/)
    .map(formatOrderColumnWord)
    .join(' ')
}

const getChannelOrderNumberLabel = (channelType: string) => {
  switch (channelType) {
    case 'primary':
      return 'Source Order #'
    case 'accounting':
      return 'Accounting Order #'
    case 'shipping':
      return 'Shipping Order #'
    case 'erp':
      return 'ERP Order #'
    case 'oms':
      return 'OMS Order #'
    default:
      return `${formatOrderColumnWord(channelType)} Order #`
  }
}

const normalizeStatusValue = (value: string | null | undefined) => {
  const normalized = (value || '').trim().toUpperCase()
  return normalized
}

const normalizePersistedColumnFilters = (columnFilters: Record<string, any> = {}) => {
  const normalizedFilters = {...columnFilters}
  if (normalizedFilters.cancel_status === true || normalizedFilters.cancel_status === 'true') {
    normalizedFilters.cancel_status = 'Cancelled'
  } else if (
    normalizedFilters.cancel_status === false ||
    normalizedFilters.cancel_status === 'false'
  ) {
    normalizedFilters.cancel_status = 'Not Cancelled'
  } else if (normalizedFilters.cancel_status === 'N/A') {
    normalizedFilters.cancel_status = 'NA'
  }
  return normalizedFilters
}

interface PaginationData {
  page: number;
  limit: number;
  search_string: string;
  status_filters: StatusFilters;
  sort_by: string;
  sort_order: string;
  api_created_at_min: string;
  api_created_at_max: string;
}

interface BulkSyncState {
  total: number;
  completed: number;
  inProgress: boolean;
  success: boolean;
  message: string;
}

const RESYNC_STATUSES = ['ERROR', 'ERROR_UNLINKED', 'ONHOLD', 'ERROR_NO_MAPPING', 'IGNORED']

const createBulkSyncState = (): BulkSyncState => ({
  total: 0,
  completed: 0,
  inProgress: false,
  success: false,
  message: '',
})

const SELECTION_STORAGE_KEY = 'orders-bulk-sync-selections'
const DEFAULT_PACKAGE_FORM: PackageFormState = {
  length: '',
  width: '',
  height: '',
  weight: '',
  weightUnit: 'KGS',
  dimensionUnit: 'MM',
}

const extractUnitCode = (unitValue: any): string => {
  if (!unitValue) return ''
  if (typeof unitValue === 'string') return unitValue
  return unitValue?.code || unitValue?.label || ''
}

const hasPackageValues = (packageDetails: any) => {
  if (!packageDetails) return false
  const values = [
    packageDetails?.PACKAGE_LENGTH,
    packageDetails?.PACKAGE_WIDTH,
    packageDetails?.PACKAGE_HEIGHT,
    packageDetails?.PACKAGE_WEIGHT,
  ]
  return values.some((value) => value !== null && value !== undefined && String(value).trim() !== '')
}

const Orders = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const {isRead, isWrite, isDelete} = usePermission();
  const {orderListLoading, orderList, orderPaginationData, invoice_status_available, cancel_status_available, shipping_label_status_available, shippingLabelsLoading, orderDocumentsDownloadLoading} = useAppSelector((state) => state.order)
  const {fetchedConnectedPlatformData, connectedPlatformTypes, connectedPlatformTypesLoading, mainStoreData} = useAppSelector((state) => state.fetchConnectedPlatform)
  const omsPlatformId = useAppSelector((state: any) => state.fetchConnectedPlatform?.omsStoreData?.id ?? null)
  const { fetchedUserAccountData } = useAppSelector(state=>state.userAccountData)
  const customerAccess = useCustomerAccess()
  const [searchParams] = useSearchParams()
  const searchParamsKey = searchParams.toString()
  let sync_status = searchParams.get('sync_status')
  let from_date = searchParams.get('from_date')
  let to_date = searchParams.get('to_date')
  let platform_id = searchParams.get('platform_id')
  let from_source = searchParams.get('from')
  const isDashboardCardLink = from_source === 'dashboard_card'
  const initialDashboardPendingAction = isDashboardCardLink
    ? parseDashboardPendingOrderActionFromSearchParams(searchParams)
    : null

  // localStorage key for persisting filters - unique per user
  const getFiltersStorageKey = () => {
    const userId = fetchedUserAccountData?.id;
    if (!userId) return null;
    return `orders-all-filters-${userId}`;
  };

  // Load persisted filters from localStorage on mount
  const loadPersistedFilters = () => {
    const storageKey = getFiltersStorageKey();
    if (!storageKey) {
      return {
        selectedPlatform: '',
        selectedStarDate: '',
        selectedEndDate: '',
        api_created_at_min: '',
        api_created_at_max: '',
        columnFilters: {},
      };
    }
    
    try {
      const savedFilters = localStorage.getItem(storageKey);
      if (savedFilters) {
        const parsed = JSON.parse(savedFilters);
        return {
          selectedPlatform: parsed.selectedPlatform || '',
          selectedStarDate: parsed.selectedStarDate || '',
          selectedEndDate: parsed.selectedEndDate || '',
          api_created_at_min: parsed.api_created_at_min || '',
          api_created_at_max: parsed.api_created_at_max || '',
          columnFilters: parsed.columnFilters || {},
        };
      }
    } catch (error) {
      console.error('Error loading persisted filters:', error);
    }
    return {
      selectedPlatform: '',
      selectedStarDate: '',
      selectedEndDate: '',
      api_created_at_min: '',
      api_created_at_max: '',
      columnFilters: {},
    };
  };

  const getInitialOrdersFilters = () => {
    const persistedFilters = loadPersistedFilters()
    const normalizedPersistedColumnFilters = normalizePersistedColumnFilters(
      persistedFilters.columnFilters || {}
    )

    const initialValues = {
      selectedPlatform: persistedFilters.selectedPlatform || '',
      selectedStarDate: persistedFilters.selectedStarDate || '',
      selectedEndDate: persistedFilters.selectedEndDate || '',
      api_created_at_min: persistedFilters.api_created_at_min || '',
      api_created_at_max: persistedFilters.api_created_at_max || '',
      columnFilters: normalizedPersistedColumnFilters,
    }

    // Deep-link override is only honored for dashboard card navigation.
    if (isDashboardCardLink) {
      initialValues.selectedPlatform = ''
      initialValues.selectedStarDate = ''
      initialValues.selectedEndDate = ''
      initialValues.api_created_at_min = from_date ?? ''
      initialValues.api_created_at_max = to_date ?? ''
      initialValues.columnFilters = {}
    }

    return initialValues
  }

  const getStoredOrdersFilters = () => {
    const persistedFilters = loadPersistedFilters()
    return {
      selectedPlatform: persistedFilters.selectedPlatform || '',
      selectedStarDate: persistedFilters.selectedStarDate || '',
      selectedEndDate: persistedFilters.selectedEndDate || '',
      api_created_at_min: persistedFilters.api_created_at_min || '',
      api_created_at_max: persistedFilters.api_created_at_max || '',
      columnFilters: normalizePersistedColumnFilters(persistedFilters.columnFilters || {}),
    }
  }

  const initialOrdersFilters = getInitialOrdersFilters()

  const [selectedSyncStatus, setSelectSyncStatus] = useState(sync_status ?? '')
  const [pendingDashboardAction, setPendingDashboardAction] =
    useState<DashboardPendingOrderAction | null>(initialDashboardPendingAction)
  const deepLinkAppliedRef = useRef(!(isDashboardCardLink && initialDashboardPendingAction))
  const [selectedPlatform, setSelectedPlatform] = useState(initialOrdersFilters.selectedPlatform)
  const [selectedShipmentStatus, setSelectedShipmentStatus] = useState('')
  const [selectedStarDate, setSelectedStartDate] = useState(initialOrdersFilters.selectedStarDate)
  const {successToast, errorToast,warningToast} = useAppToast()
  const [selectedEndDate, setSelectedEndDate] = useState(initialOrdersFilters.selectedEndDate)
  const [showDateRangePicker, setShowDateRangePicker] = useState(false)
  const [searchString, setSearchString] = useState('')
  const [paginationData, setPaginationData] = useState<PaginationData>({
    page: 1,
    limit: 10,
    search_string: '',
    status_filters: initialOrdersFilters.columnFilters,
    sort_by: 'api_created_at',
    sort_order: 'DESC',
    api_created_at_min: initialOrdersFilters.api_created_at_min,
    api_created_at_max: initialOrdersFilters.api_created_at_max,
  })
  const [filters, setFilters] = useState(initialOrdersFilters.columnFilters);
  const [showBulkSyncModal, setShowBulkSyncModal] = useState(false)
  const [showUploadOrdersCsvModal, setShowUploadOrdersCsvModal] = useState(false)
  const [clearSelectedRowsFlag, setClearSelectedRowsFlag] = useState(false)
  const [bulkSyncMeta, setBulkSyncMeta] = useState<BulkSyncState>(createBulkSyncState)
  // OMS Modal States
  const [showOmsPackageModal, setShowOmsPackageModal] = useState(false)
  const [selectedOmsPackageOrder, setSelectedOmsPackageOrder] = useState<any>(null)

  // OMS Direct Shipment Modal States
  const [showDirectShipmentModal, setShowDirectShipmentModal] = useState(false)
  const [selectedDirectShipmentOrder, setSelectedDirectShipmentOrder] = useState<any>(null)

  // TEMU Old Modal States
  const [showPackageModal, setShowPackageModal] = useState(false)
  const [packageModalLoading, setPackageModalLoading] = useState(false)
  const [packageSaving, setPackageSaving] = useState(false)
  const [packageModalMode, setPackageModalMode] = useState<'create' | 'update'>('create')
  const [selectedPackageOrder, setSelectedPackageOrder] = useState<any>(null)
  const [packageLineItems, setPackageLineItems] = useState<any[]>([])
  const [packageForm, setPackageForm] = useState<PackageFormState>(DEFAULT_PACKAGE_FORM)
  const [requestShippingLabelLoading, setRequestShippingLabelLoading] = useState(false)
  const [invoiceDownloadLoading, setInvoiceDownloadLoading] = useState(false)

  const getSelectionsStorageKey = () => {
    const userId = fetchedUserAccountData?.id
    return userId ? `${SELECTION_STORAGE_KEY}-${userId}` : SELECTION_STORAGE_KEY
  }

  const getSelectionsFromStorage = (): {
    selectedOrderIds: number[]
    selectedOrders: {[id: number]: any}
  } => {
    try {
      const storageKey = getSelectionsStorageKey()
      const stored = storageKey ? localStorage.getItem(storageKey) : null
      if (stored) {
        const parsed = JSON.parse(stored)
        return {
          selectedOrderIds: parsed.selectedOrderIds || [],
          selectedOrders: parsed.selectedOrders || {},
        }
      }
      return {selectedOrderIds: [], selectedOrders: {}}
    } catch (error) {
      console.error('Error reading selections from localStorage:', error)
      return {selectedOrderIds: [], selectedOrders: {}}
    }
  }

  const saveSelectionsToStorage = (selectedOrderIds: number[], selectedOrders: {[id: number]: any}) => {
    try {
      const storageKey = getSelectionsStorageKey()
      if (!storageKey) return
      const dataToSave = {
        selectedOrderIds,
        selectedOrders,
      }
      localStorage.setItem(storageKey, JSON.stringify(dataToSave))
    } catch (error) {
      console.error('Error saving selections to localStorage:', error)
    }
  }

  const clearSelectionsFromStorage = () => {
    try {
      const storageKey = getSelectionsStorageKey()
      if (storageKey && localStorage.getItem(storageKey)) {
        localStorage.removeItem(storageKey)
      }
    } catch (error) {
      console.error('Error clearing selections from localStorage:', error)
    }
  }

  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>(() => {
    const {selectedOrderIds} = getSelectionsFromStorage()
    return selectedOrderIds || []
  })

  const selectedOrderIdsSet = useMemo(() => new Set(selectedOrderIds), [selectedOrderIds])

  const [selectedOrdersMap, setSelectedOrdersMap] = useState<Record<number, any>>(() => {
    const {selectedOrders} = getSelectionsFromStorage()
    return selectedOrders || {}
  })

  useEffect(() => {
    if (!isDashboardCardLink) return

    const deepLinkAction = parseDashboardPendingOrderActionFromSearchParams(searchParams)
    const deepLinkFromDate = searchParams.get('from_date') ?? ''
    const deepLinkToDate = searchParams.get('to_date') ?? ''

    deepLinkAppliedRef.current = !deepLinkAction
    setPendingDashboardAction(deepLinkAction)
    setSelectedPlatform('')
    setSelectedStartDate(deepLinkFromDate)
    setSelectedEndDate(deepLinkToDate)
    setSearchString('')
    setFilters({})
    setPaginationData((prev) => ({
      ...prev,
      page: 1,
      search_string: '',
      status_filters: {},
      api_created_at_min: deepLinkFromDate,
      api_created_at_max: deepLinkToDate,
    }))
  }, [searchParamsKey, isDashboardCardLink])

  useEffect(() => {
    if (isDashboardCardLink) return

    const storedFilters = getStoredOrdersFilters()
    deepLinkAppliedRef.current = true
    setPendingDashboardAction(null)
    setSelectedPlatform(storedFilters.selectedPlatform)
    setSelectedStartDate(storedFilters.selectedStarDate)
    setSelectedEndDate(storedFilters.selectedEndDate)
    setFilters(storedFilters.columnFilters)
    setPaginationData((prev) => ({
      ...prev,
      page: 1,
      api_created_at_min: storedFilters.api_created_at_min,
      api_created_at_max: storedFilters.api_created_at_max,
      status_filters: storedFilters.columnFilters,
    }))
  }, [searchParamsKey, isDashboardCardLink, fetchedUserAccountData?.id])

  useEffect(() => {
    saveSelectionsToStorage(selectedOrderIds, selectedOrdersMap)
  }, [selectedOrderIds, selectedOrdersMap])

  // Keep selected row payloads fresh with latest page data.
  // This prevents stale shipping_label_status values from disabling bulk actions.
  useEffect(() => {
    if (!orderList?.length) return
    if (!selectedOrderIds?.length) return

    const selectedIdsSet = new Set(selectedOrderIds)
    setSelectedOrdersMap((prev) => {
      const next = {...prev}
      let changed = false

      orderList.forEach((row: any) => {
        const rowId = row?.id
        if (!rowId) return

        if (selectedIdsSet.has(rowId)) {
          if (next[rowId] !== row) {
            next[rowId] = row
            changed = true
          }
          return
        }

        if (next[rowId]) {
          delete next[rowId]
          changed = true
        }
      })

      return changed ? next : prev
    })
  }, [orderList, selectedOrderIds])

  const isRestrictedAccess = customerAccess?.isRestricted
  const isReadOnlyAccess = customerAccess?.isReadOnly
  const bulkSyncProgress = bulkSyncMeta.total
    ? Math.round((bulkSyncMeta.completed / bulkSyncMeta.total) * 100)
    : 0

  const clearAllSelections = useCallback(() => {
    setSelectedOrderIds([])
    setSelectedOrdersMap({})
    clearSelectionsFromStorage()
    setClearSelectedRowsFlag((prev) => !prev)
  }, [])

  // Save filters to localStorage whenever they change
  useEffect(() => {
    // Dashboard card filters are transient and should not replace user's normal saved filter state.
    if (isDashboardCardLink) return;

    const storageKey = getFiltersStorageKey();
    if (!storageKey) return;
    
    const filtersToSave = {
      selectedPlatform,
      selectedStarDate,
      selectedEndDate,
      api_created_at_min: paginationData.api_created_at_min,
      api_created_at_max: paginationData.api_created_at_max,
      columnFilters: filters,
    };
    localStorage.setItem(storageKey, JSON.stringify(filtersToSave));
  }, [selectedPlatform, selectedStarDate, selectedEndDate, paginationData.api_created_at_min, paginationData.api_created_at_max, filters, fetchedUserAccountData?.id, isDashboardCardLink]);

  // Clear selections on page navigation or reload
  useEffect(() => {
    return () => {
      clearAllSelections()
    }
  }, [clearAllSelections])

  const isResyncableRow = (row: any) => {
    if (!row?.platformorder_set?.length) {
      return false
    }
    return row.platformorder_set.some((item: any) => RESYNC_STATUSES.includes(item?.sync_status))
  }

  const collectResyncTargets = (rows: any[]): {id: number; status: string}[] => {
    const targets: {id: number; status: string}[] = []
    rows?.forEach((row: any) => {
      row?.platformorder_set
        ?.filter((item: any) => RESYNC_STATUSES.includes(item?.sync_status) && item?.id)
        ?.forEach((item: any) => {
          targets.push({id: item.id, status: item.sync_status})
        })
    })
    return targets
  }

  const isInvoiceEligibleOrder = (row: any) => {
    return Boolean(row?.id)
  }

  const selectedFailedOrders = useMemo(() => {
    const allOrders = Object.values(selectedOrdersMap)
    return collectResyncTargets(allOrders)
  }, [selectedOrdersMap])

  const selectedInvoiceOrders = useMemo(() => {
    const allOrders = Object.values(selectedOrdersMap)
    return allOrders.filter((order: any) => order?.id && isInvoiceEligibleOrder(order))
  }, [selectedOrdersMap])

  // Check which selected orders have valid shipping labels
  const selectedOrdersWithLabels = useMemo(() => {
    const allOrders = Object.values(selectedOrdersMap)
    return allOrders.filter((order: any) => {
      // Check if label_status is 'success' or 'SUCCESS' and label_url is not null
      const labelStatus = order?.shipping_label_status?.label_status
      const labelUrl = order?.shipping_label_status?.label_url
      return labelStatus && (labelStatus.toLowerCase() === 'success') && labelUrl
    })
  }, [selectedOrdersMap])

  // Check which selected orders can request new shipping labels (N/A or FAILED status)
  const selectedOrdersForNewLabel = useMemo(() => {
    const allOrders = Object.values(selectedOrdersMap)
    return allOrders.filter((order: any) => {
      if (!canRequestNewShippingLabel(order?.platform?.name)) return false

      const labelStatus = order?.shipping_label_status?.label_status
      const normalizedStatus = labelStatus
        ?.toString()
        .replace(/['"]/g, '')
        .trim()
        .toUpperCase()

      // Allow if label_status is null/empty, "N/A", "NA", or "FAILED"
      return !labelStatus || normalizedStatus === 'N/A' || normalizedStatus === 'NA' || normalizedStatus === 'FAILED'
    })
  }, [selectedOrdersMap])

  const showRequestNewShippingLabelAction = useMemo(() => {
    const allOrders = Object.values(selectedOrdersMap)
    if (allOrders.length > 0) {
      return allOrders.some((order: any) => canRequestNewShippingLabel(order?.platform?.name))
    }

    if (selectedPlatform) {
      const filteredPlatform = fetchedConnectedPlatformData?.find(
        (platform: any) => String(platform?.id) === String(selectedPlatform)
      )
      return canRequestNewShippingLabel(filteredPlatform?.name)
    }

    return hasConnectedRequestNewShippingLabelPlatform(fetchedConnectedPlatformData)
  }, [selectedOrdersMap, selectedPlatform, fetchedConnectedPlatformData])

  const selectedOrdersForDocumentDownload = useMemo(
    () => filterOrdersForDocumentDownload(Object.values(selectedOrdersMap)),
    [selectedOrdersMap]
  )

  const selectedOrdersForAmazonVendorDocumentDownload = useMemo(
    () => filterOrdersForAmazonVendorDocumentDownload(Object.values(selectedOrdersMap)),
    [selectedOrdersMap]
  )

  const selectedLegacyOrdersWithLabels = useMemo(
    () =>
      selectedOrdersWithLabels.filter((order: any) =>
        isLegacyShippingLabelPlatform(order?.platform?.name)
      ),
    [selectedOrdersWithLabels]
  )

  // Check if shipping label actions should be shown (Temu / Amazon Vendor or show_action=true)
  const showShippingLabelActions = useMemo(() => {
    const showActionParam = searchParams.get('show_action')
    if (showActionParam === 'true') return true
    
    if (!shipping_label_status_available) return false
    
    if (!hasConnectedShippingLabelPlatform(fetchedConnectedPlatformData)) return false
    
    const allOrders = Object.values(selectedOrdersMap)
    return allOrders.some((order: any) => isShippingLabelPlatform(order?.platform?.name))
  }, [selectedOrdersMap, searchParams, shipping_label_status_available, fetchedConnectedPlatformData])

  const showOrderDocumentDownloadActions = useMemo(
    () =>
      resolveShowOrderDocumentDownloadActions({
        searchParams,
        connectedPlatforms: fetchedConnectedPlatformData,
        selectedOrdersMap,
        selectedPlatform,
      }),
    [searchParams, fetchedConnectedPlatformData, selectedOrdersMap, selectedPlatform]
  )

  const showAmazonVendorLabelDocumentDownloadActions = useMemo(
    () =>
      resolveShowAmazonVendorLabelDocumentDownloadActions({
        searchParams,
        connectedPlatforms: fetchedConnectedPlatformData,
        selectedOrdersMap,
        selectedPlatform,
      }),
    [searchParams, fetchedConnectedPlatformData, selectedOrdersMap, selectedPlatform]
  )

  const showLegacyShippingLabelActions = useMemo(
    () =>
      resolveShowLegacyShippingLabelActions({
        showShippingLabelActions,
        connectedPlatforms: fetchedConnectedPlatformData,
        selectedOrdersMap,
        selectedPlatform,
      }),
    [
      showShippingLabelActions,
      fetchedConnectedPlatformData,
      selectedOrdersMap,
      selectedPlatform,
    ]
  )

  useEffect(() => {
    if (bulkSyncMeta.total > 0 && !bulkSyncMeta.inProgress) {
      const timer = setTimeout(() => {
        setBulkSyncMeta(createBulkSyncState())
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [bulkSyncMeta.total, bulkSyncMeta.inProgress])

  useEffect(() => {
    if (selectedFailedOrders.length === 0 && !bulkSyncMeta.inProgress && bulkSyncMeta.total === 0) {
      setBulkSyncMeta(createBulkSyncState())
    }
  }, [selectedFailedOrders.length, bulkSyncMeta.inProgress, bulkSyncMeta.total])

  const handleResetFilters = () => {
   setPaginationData(prev => ({
    ...prev,
    page: 1,
    limit: 10,
    search_string: '',
    status_filters: Object.keys(prev.status_filters || {}).reduce((acc, key) => {
      acc[key] = '';
      return acc;
    }, {}),
    sort_by: 'api_created_at',
    sort_order: 'DESC',
    api_created_at_min: '',
    api_created_at_max: '',
  }));
    setSelectedPlatform('');
    // setSelectSyncStatus('');
    setSelectedShipmentStatus('');
    setSelectedStartDate('');
    setSelectedEndDate('');
    setSearchString('');
    setFilters({});
    clearAllSelections();
    setBulkSyncMeta(createBulkSyncState());
    // Clear persisted filters from localStorage
    const storageKey = getFiltersStorageKey();
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }
  };

  const packageIconParam = (
    searchParams.get('package_icon') ||
    searchParams.get('show_package_icon') ||
    ''
  ).toLowerCase()
  const showPackageIconForAll = ['1', 'true', 'yes', 'all'].includes(packageIconParam)

  const shouldShowPackageAction = (row: any) => {
    if (showPackageIconForAll) return true
    return isShippingLabelPlatform(row?.platform?.name)
  }

  const normalizeLineItems = (lineItems: any[] = []) => {
    if (!Array.isArray(lineItems)) return []
    return lineItems.map((item: any) => ({
      ...item,
      name: item?.name || item?.title || 'N/A',
      sku: item?.sku || item?.platform_product_variant?.sku || 'N/A',
      barcode:
        item?.barcode ||
        item?.platform_product_variant?.barcode ||
        item?.platform_product_variant?.gtin_number ||
        'N/A',
      quantity: item?.quantity ?? 0,
    }))
  }

  const extractLineItemsFromOrderPayload = (payload: any) =>
    normalizeLineItems(
      payload?.line_items ||
        payload?.platform_order_line_items ||
        payload?.data?.line_items ||
        payload?.data?.platform_order_line_items ||
        payload?.platform_order?.line_items ||
        []
    )

      // Open OMS Package Modal
  const handleOpenOmsPackageModal = async (row: any) => {
    if (!row?.id) return
    setSelectedOmsPackageOrder(row)
    setShowOmsPackageModal(true)
  }

  const handleCloseOmsPackageModal = () => {
    setShowOmsPackageModal(false)
    setSelectedOmsPackageOrder(null)
  }

  // Open OMS Direct Shipment Modal
  const handleOpenDirectShipmentModal = (row: any) => {
    if (!row?.id) return
    setSelectedDirectShipmentOrder(row)
    setShowDirectShipmentModal(true)
  }

  const handleCloseDirectShipmentModal = () => {
    setShowDirectShipmentModal(false)
    setSelectedDirectShipmentOrder(null)
  }

  const handleOpenPackageModal = async (row: any) => {
    if (!row?.id) return
    setShowPackageModal(true)
    setPackageModalLoading(true)
    setPackageModalMode('create')
    setSelectedPackageOrder(row)
    setPackageForm(DEFAULT_PACKAGE_FORM)
    setPackageLineItems(normalizeLineItems(row?.line_items || row?.platform_order_line_items || []))

    try {
      const response: any = await dispatch(getOrderPackageByOrderIdAsync(row.id))
      if (response?.meta?.requestStatus !== 'fulfilled') {
        const statusCode = response?.payload?.status || response?.error?.status
        const apiMessage =
          response?.payload?.data?.message ||
          response?.payload?.data?.massage ||
          response?.payload?.message ||
          response?.error?.message ||
          ''
        const isNoPackageCase =
          statusCode === 404 ||
          /not\s*found|no\s*package|does\s*not\s*exist/i.test(String(apiMessage))

        if (isNoPackageCase) {
          setPackageModalMode('create')
          return
        }
        throw new Error('Failed to fetch package details.')
      }

      const orderData = response?.payload?.data || response?.payload || {}
      const packageDetails = orderData?.package_details || {}
      const nextForm: PackageFormState = {
        length:
          packageDetails?.PACKAGE_LENGTH === null || packageDetails?.PACKAGE_LENGTH === undefined
            ? ''
            : String(packageDetails.PACKAGE_LENGTH),
        width:
          packageDetails?.PACKAGE_WIDTH === null || packageDetails?.PACKAGE_WIDTH === undefined
            ? ''
            : String(packageDetails.PACKAGE_WIDTH),
        height:
          packageDetails?.PACKAGE_HEIGHT === null || packageDetails?.PACKAGE_HEIGHT === undefined
            ? ''
            : String(packageDetails.PACKAGE_HEIGHT),
        weight:
          packageDetails?.PACKAGE_WEIGHT === null || packageDetails?.PACKAGE_WEIGHT === undefined
            ? ''
            : String(packageDetails.PACKAGE_WEIGHT),
        weightUnit: extractUnitCode(packageDetails?.WEIGHT_UNIT) || 'KGS',
        dimensionUnit: extractUnitCode(packageDetails?.DIMENSION_UNIT) || 'MM',
      }

      setSelectedPackageOrder({...row, ...orderData})
      const packageLineItems = extractLineItemsFromOrderPayload(orderData)
      if (packageLineItems.length) {
        setPackageLineItems(packageLineItems)
      }
      setPackageForm(nextForm)
      setPackageModalMode(hasPackageValues(packageDetails) ? 'update' : 'create')
    } catch (error: any) {
      console.error('Unable to load package data', error)
      warningToast('Could not load package details. You can still create package data.')
      setPackageModalMode('create')
    } finally {
      setPackageModalLoading(false)
    }
  }

  const handleClosePackageModal = () => {
    setShowPackageModal(false)
    setPackageModalLoading(false)
    setPackageSaving(false)
    setPackageModalMode('create')
    setSelectedPackageOrder(null)
    setPackageLineItems([])
    setPackageForm(DEFAULT_PACKAGE_FORM)
  }

  const handleSavePackage = async () => {
    if (!selectedPackageOrder?.id) return

    const length = Number(packageForm.length)
    const width = Number(packageForm.width)
    const height = Number(packageForm.height)
    const weight = Number(packageForm.weight)

    if (!length || !width || !height || !weight) {
      warningToast('Please enter valid package dimensions and weight.')
      return
    }

    setPackageSaving(true)
    try {
      const payload = {
        order_id: selectedPackageOrder.id,
        package_length: length,
        package_width: width,
        package_height: height,
        package_weight: weight,
        weight_unit: packageForm.weightUnit || 'KGS',
        dimension_unit: packageForm.dimensionUnit || 'MM',
      }
      const response: any = await dispatch(saveOrderPackageAsync(payload))
      if (response?.meta?.requestStatus !== 'fulfilled') {
        throw new Error(response?.payload?.data?.message || response?.payload?.message || 'Rejected')
      }
      successToast(response?.payload?.massage || response?.payload?.message || 'Package saved successfully.')
      handleClosePackageModal()
    } catch (error: any) {
      console.error('Failed to save package', error)
      errorToast(error?.message || 'Failed to save package.')
    } finally {
      setPackageSaving(false)
    }
  }

  //resync component start
  const ResyncButton = ({orderId, IsOnhold, userId }) => {
    const [showWarning, setShowWarning] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handlePerformResync = async () => {
      setIsSubmitting(true);

      try {
        const action = IsOnhold
          ? onholdOrderResyncAsync({ hold_order_ids: [orderId] })
          : orderResyncAsync({ failed_order_ids: [orderId] });
        await dispatch(action).unwrap();
        // Common success handling
        dispatch(getOrdersListAsync({paramData: { ...paginationData },selectedPlatform}));
        successToast(IsOnhold
            ? 'Hold order(s) updated successfully for ready to sync.'
            : 'Failed Order resyncing started, status changed to Pending'
        );
      } catch (error) {
        console.error('Error during order resync:', error);
        toast.error('Order resync failed. Please try again.');
      } finally {
        setShowWarning(false);
        setIsSubmitting(false);
      }
    }

    const warningText =
      'Please check the Source Platform to confirm this order was not created there already. Only attempt a resync if the order is genuinely missing from your Source Platform.';

    return (
      <Fragment>
        {shouldShowAction(customerAccess, 'edit', userId) && (
          <>
            <OverlayTrigger
              placement='top'
              overlay={
                <Tooltip id={`tooltip-${orderId}`}>Click to retry syncing the Order</Tooltip>
              }
            >
              <Button
                className='ms-2'
                variant='link'
                {...getActionButtonProps(customerAccess, 'edit', userId)}
                style={{
                  padding: '5px 10px',
                  cursor: customerAccess.isReadOnly ? 'not-allowed' : 'pointer',
                }}
                onClick={() => setShowWarning(true)}
              >
                <FontAwesomeIcon
                  icon={faRotate}
                  color={IsOnhold ? 'darkslategray' : 'red'}
                  size='lg'
                />
              </Button>
            </OverlayTrigger>

            <Modal show={showWarning} onHide={() => setShowWarning(false)} centered>
              <Modal.Header closeButton className='bg-warning bg-opacity-10'>
                <Modal.Title className='text-warning fw-bold'>
                  <FontAwesomeIcon icon={faInfoCircle} className='me-2 mt-1 text-warning' />
                   Important Check
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className='d-flex align-items-start'>
                  <p className='mb-0'>{warningText}</p>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant='outline-secondary'
                  onClick={() => setShowWarning(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button variant='primary' onClick={handlePerformResync} disabled={isSubmitting}>
                  {isSubmitting ? 'Processing...' : 'Proceed with Resync'}
                </Button>
              </Modal.Footer>
            </Modal>
          </>
        )}
      </Fragment>
    )
  }

useEffect(() => {
  const fetchConnectedPlatforms = async () => {
    try {
      const response = await dispatch(getAllPlatformTypesAsync()).unwrap();
      const data = response?.metadata?.logs?.order_logs?.fields || []
      const resolvedDeepLinkFilters =
        pendingDashboardAction && !deepLinkAppliedRef.current
          ? resolveDashboardPendingActionFilters(pendingDashboardAction, {
              metadataFields: data,
              connectedPlatforms: fetchedConnectedPlatformData,
              isOmsView: false,
            })
          : null
      const shouldApplyDeepLink =
        isDashboardCardLink && !!resolvedDeepLinkFilters && !deepLinkAppliedRef.current

      const columnKeys = data
        .map((channel: any) => channel?.column_search)
        .filter((key: string | undefined) => Boolean(key))

      const allFilterKeys: string[] = Array.from(new Set(columnKeys))

      setFilters((prev) => {
        if (shouldApplyDeepLink && resolvedDeepLinkFilters) {
          const merged = buildDashboardDeepLinkColumnFilters(allFilterKeys, resolvedDeepLinkFilters)
          return isShallowEqual(prev, merged) ? prev : merged
        }

        const merged = {...prev}

        allFilterKeys.forEach((key: string) => {
          if (merged[key] === undefined) {
            merged[key] = ''
          }
        })

        return isShallowEqual(prev, merged) ? prev : merged
      })

      setPaginationData((prev) => {
        if (shouldApplyDeepLink && resolvedDeepLinkFilters) {
          const mergedStatus = buildDashboardDeepLinkColumnFilters(
            allFilterKeys,
            resolvedDeepLinkFilters
          )
          const sameStatus = isShallowEqual(prev.status_filters || {}, mergedStatus)
          if (sameStatus && prev.page === 1) {
            return prev
          }
          return {
            ...prev,
            page: 1,
            status_filters: mergedStatus,
          }
        }

        const mergedStatus = {...(prev.status_filters || {})}

        allFilterKeys.forEach((key: string) => {
          if (mergedStatus[key] === undefined) {
            mergedStatus[key] = ''
          }
        })

        const sameStatus = isShallowEqual(prev.status_filters || {}, mergedStatus)
        if (sameStatus) {
          return prev
        }

        return {
          ...prev,
          status_filters: mergedStatus,
        }
      })

      if (shouldApplyDeepLink) {
        deepLinkAppliedRef.current = true
        setPendingDashboardAction(null)
      }
    } catch (error) {
      console.error('Failed to fetch platform types:', error);
    }
  };

  fetchConnectedPlatforms();
}, [dispatch, isDashboardCardLink, pendingDashboardAction, platform_id, searchParamsKey, fetchedConnectedPlatformData]);

useEffect(() => {
  // Transform filter values for API
  const transformedFilters: any = { ...filters };
  
  // Map cancellation status filter values to boolean
  if (transformedFilters.cancel_status === 'Cancelled') {
    transformedFilters.cancel_status = true;
  } else if (transformedFilters.cancel_status === 'Not Cancelled') {
    transformedFilters.cancel_status = false;
  } else if (transformedFilters.cancel_status === '' || transformedFilters.cancel_status === undefined) {
    // Remove cancel_status if it's empty or undefined
    delete transformedFilters.cancel_status;
  }
  
  setPaginationData((prev) => {
    const sameStatus = isShallowEqual(prev.status_filters || {}, transformedFilters || {});
    const samePage = prev.page === 1;
    if (sameStatus && samePage) return prev;
    return {
      ...prev,
      page: 1,
      status_filters: transformedFilters,
    };
  });
}, [filters]);

const FilterDropdown = ({ filters, setFilters, options, hasActiveFilter}) => {
  const [show, setShow] = useState(false);
  const iconRef = useRef(null);

  const handleSelectChange = (e, key) => {
    setFilters((prev) => ({
      ...prev,
      [key]: e.target.value,
    }));
  };
  return (
    <>
      <span
        ref={iconRef}
        style={{
          cursor: "pointer",
          color: hasActiveFilter ? "blue" : "darkgrey",
        }}
        onClick={() => setShow(!show)}
      >
        <FontAwesomeIcon icon={faFilter} />
      </span>

      <Overlay
        target={iconRef.current}
        show={show}
        placement="bottom-end"
        rootClose
        onHide={() => setShow(false)}
      >
        <Popover id="filter-popover" className="p-2" style={{ minWidth: "220px" }}>
          <Popover.Body>
            {options.map(({ label, key, values }) => (
              <Form.Group key={key} className="mb-2">
                <Form.Label className="mb-1">{label}</Form.Label>
                <Form.Select
                  size="sm"
                  value={filters[key] || ""}
                  onChange={(e) => handleSelectChange(e, key)}
                >
                  <option value="">All</option>
                  {values.map((val, idx) => {
                    const optionValue = typeof val === 'object' ? val.value : val;
                    const optionLabel = typeof val === 'object' ? (val.label ?? val.value) : val;
                    return (
                      <option key={idx} value={optionValue}>
                        {optionLabel}
                      </option>
                    );
                  })}
                </Form.Select>
              </Form.Group>
            ))}
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  );
};

const filterableHeaderStyle: React.CSSProperties = {
  alignItems: 'flex-start',
  display: 'flex',
  gap: '6px',
  justifyContent: 'space-between',
  lineHeight: 1.25,
  whiteSpace: 'normal',
  width: '100%',
}

const filterableHeaderLabelStyle: React.CSSProperties = {
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'visible',
  textOverflow: 'clip',
  whiteSpace: 'normal',
  wordBreak: 'normal',
  overflowWrap: 'anywhere',
}

const filterableHeaderActionsStyle: React.CSSProperties = {
  alignItems: 'flex-start',
  display: 'inline-flex',
  flex: '0 0 auto',
  gap: '6px',
  lineHeight: 1,
  paddingTop: '2px',
}


console.log({filters, paginationData})
   //resync component start
   const ShippmentResyncButton = ({shippingId, userId}) => {
    const handleResyncClick = async () => {

      // Dispatch your API call action here, passing the orderId

      try {
        // Dispatch your API call action here, passing the orderId
        dispatch(shippingResyncAsync({failed_shipping_ids: [shippingId]})).then((response) => {
          dispatch(
            getOrdersListAsync({paramData: {...paginationData}, selectedPlatform: selectedPlatform})
          )

          // Show toast notification after successful dispatch
          successToast(response?.payload?.message)
          // successToast("Failed Shipment resyncing started, status changed to Pending")
        })
      } catch (error: any) {
        console.error('Error during order resync:', error)
        toast.error(error?.message || 'Shipment resync failed. Please try again.')
      }
    }

    return (
      <Fragment>
        {shouldShowAction(customerAccess, 'edit', userId) && (
          <OverlayTrigger
            placement='top'
            overlay={<Tooltip id={`tooltip-${shippingId}`}>Click to retry the shipment</Tooltip>}
          >
            <Button
              className='ms-2'
              variant='link'
              {...getActionButtonProps(customerAccess, 'edit', userId)}
              style={{padding: '5px 10px', cursor: customerAccess.isReadOnly ? 'not-allowed' : 'pointer'}}
              onClick={handleResyncClick}
            >
              <FontAwesomeIcon icon={faRotate} color='red' size='lg' />
            </Button>
          </OverlayTrigger>
        )}
      </Fragment>
    )
  }
  
  const renderPlatformStatusCell = (platformId, row, type = 'order') => {
  const platformOrder = row.platformorder_set?.find(
    (item) => item.platform === platformId
  );

  const status = type === 'invoice'
    ? (platformOrder?.invoices?.sync_status ?? row?.invoices?.sync_status)
    : (platformOrder?.sync_status ?? 'PENDING');

  const message = type === 'invoice'
    ? (platformOrder?.invoices?.sync_message ?? row?.invoices?.sync_message)
    : (platformOrder?.sync_message ?? 'N/A');

  const resyncId = type === 'invoice'
    ? platformOrder?.invoices?.id
    : platformOrder?.id;

  const isResyncable = RESYNC_STATUSES.includes(status);
  const IsOnhold = status === 'ONHOLD';

  return (
    <div className="d-flex align-items-center w-100 justify-content-center">
      <StatusButton syncStatus={status} syncMessage={message} />
      
      {(type !== 'invoice' && isWrite && isResyncable) && (<ResyncButton orderId={resyncId} IsOnhold={IsOnhold} userId={row?.user_id} />)}    </div>
  );
};

const arraysEqual = (a: number[], b: number[]) => {
  if (a.length !== b.length) return false
  const setA = new Set(a)
  for (const val of b) {
    if (!setA.has(val)) return false
  }
  return true
}

// Generate Channel Order Status Columns
const generateChannelStatusColumn = ({
  column_data,
}: {
  column_data: any
}) => {
if (!column_data) return null 

  const {
    column_search: statusKey,
    display_name,
    column_search_values: filterOptions,
    platform_id,
    channel_type
  } = column_data

  // For Shipment Status
 const renderShipmentCell = (row) => {
      const shipmentSummary = getShipmentSyncSummary(row?.shipments)
      const showResyncButton = row?.shipment_status === 'ERROR'
      return (
        <div className="d-flex align-items-center">
          <ShipmentStatusButton
            syncStatus={row?.shipment_status}
            syncMessage={shipmentSummary.aggregatedMessage}
          />
          {(isWrite && showResyncButton && shipmentSummary.resyncShippingId) && (
            <ShippmentResyncButton shippingId={shipmentSummary.resyncShippingId} userId={row?.user_id} />
          )}
        </div>
      )
  }

  // For Shipping Label Status
  const renderShippingLabelCell = (row) => {
    return (
      <div className="d-flex align-items-center">
        <StatusButton
          syncStatus={row?.shipping_label_status?.label_status}
          syncMessage={row?.shipping_label_status?.label_sync_message}
        />
      </div>
    )
  }

  const resolvedDisplayName = formatOrderColumnDisplayName(display_name)
  const shipmentStatusTooltip = "Indicates if the shipping information and tracking numbers have been successfully synchronized between platforms"  ;
const sourceOrderStatusTooltip = "The current lifecycle stage of the order within this system (e.g., Pending, Processed, or Shipped)";

  const column_width = {
    primary : '170px',
    accounting : '235px',
    shipping : '225px',
    oms : '220px',
    erp : '220px',
    shipment : '165px',
    invoice : '165px',
    shipping_label : '185px'
  }

  // Determine which cell renderer to use
  const getCellRenderer = () => {
    if (channel_type === 'shipment') return renderShipmentCell;
    if (channel_type === 'shipping_label') return renderShippingLabelCell;
    return (row) => renderPlatformStatusCell(platform_id, row, channel_type === 'invoice' ? 'invoice' : 'order');
  }

  const normalizedFilterOptions = (() => {
    const options = Array.isArray(filterOptions) ? [...filterOptions] : []

    const hasOption = (targetValue: string) => options.some((opt: any) => {
      const value = typeof opt === 'object' ? (opt?.value ?? opt?.label) : opt
      return String(value || '').trim().toUpperCase() === targetValue
    })

    if (channel_type === 'shipping' && !hasOption('IGNORED')) {
      options.push({value: 'IGNORED', label: 'IGNORED'})
    }

    if (channel_type === 'shipping_label' && !hasOption('PENDING')) {
      options.push({value: 'PENDING', label: 'PENDING'})
    }

    return options
  })()

  return {
    name: (
      <div style={filterableHeaderStyle}>
        <span style={filterableHeaderLabelStyle}>{resolvedDisplayName}</span>
        <span style={filterableHeaderActionsStyle}>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`${statusKey}-tooltip`}>{channel_type === "shipment" ? shipmentStatusTooltip : sourceOrderStatusTooltip}</Tooltip>}
          >
            <span
              style={{ cursor: 'pointer', color: 'darkgrey' }}
              onClick={(e) => e.stopPropagation()}
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </span>
          </OverlayTrigger>

          <FilterDropdown
            filters={filters}
            setFilters={setFilters}
            hasActiveFilter={!!paginationData.status_filters?.[statusKey]}
            options={[
              {
                label: 'Filter Status',
                key: statusKey,
                values: normalizedFilterOptions,
              },
            ]}
          />
        </span>
      </div>
    ),
    cell: getCellRenderer(),
    wrap: true,
    sortField: statusKey,
    minWidth: column_width[channel_type] ?? '190px',
    style: {justifyContent: 'center'},
  }
}

const dynamicStatusColumns = !connectedPlatformTypesLoading &&  connectedPlatformTypes?.metadata?.logs?.order_logs?.fields.length > 0 
  ? connectedPlatformTypes?.metadata?.logs?.order_logs?.fields
      .filter((column_data: any) => {
        // Skip if invoice_status_available is false AND channel_type is "invoice"
        if (!invoice_status_available && column_data.channel_type === "invoice") {
          return false;
        }
        // Skip if shipping_label_status_available is false AND channel_type is "shipping_label"
        if (!shipping_label_status_available && column_data.channel_type === "shipping_label") {
          return false;
        }
        return true;
      })
      .map((column_data: any) =>
        generateChannelStatusColumn({
          column_data,
        })
      )
  : [];

const channelOrderNumberColumns =
  !connectedPlatformTypesLoading &&
  connectedPlatformTypes?.metadata?.logs?.order_logs?.fields?.length > 0
    ? connectedPlatformTypes?.metadata.logs.order_logs.fields
        .filter((column_data: any) => {
          // skip invoice, shipment & shipping_label
          return !["invoice", "shipment", "shipping_label"].includes(column_data.channel_type);
        })
        .map((column_data: any) => {
          const {channel_type, platform_id} = column_data
          let name: string | React.ReactNode = "";
          let storeId: number | null = null;
          let minWidth = "140px";

          switch (channel_type) {
            case "primary":
               name = (
                <span className="d-inline-flex align-items-center gap-1">
                  Source Order #
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="primary-order-tooltip">The unique ID assigned to the order copy created in the Source platform.</Tooltip>
                    }
                  >
                    <span
                      style={{ cursor: "pointer", color: "darkgrey" }}
                      onClick={(e) => e.stopPropagation()} // prevent sort
                    >
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </span>
                  </OverlayTrigger>
                </span>
              );
              storeId = platform_id ?? null;
              minWidth = "140px";
              break;

            case "accounting":
              name = "Accounting Order #";
              storeId = platform_id ?? null;
              minWidth = "165px";
              break;

            case "shipping":
              name = "Shipping Order #";
              storeId = platform_id ?? null;
              minWidth = "150px";
              break;

            case "erp":
              name = "ERP Order #";
              storeId = platform_id ?? null;
              minWidth = "140px";
              break;

            case "oms":
              name = "OMS Order #";
              storeId = platform_id ?? null;
              minWidth = "140px";
              break;

            default:
              name = getChannelOrderNumberLabel(channel_type);
              storeId = platform_id ?? null;
              break;
          }

          if (!storeId) return null; // skip if no valid storeId

          return {
            name,
            cell: (row: any) => (
              <CommonColumn
                rowData={
                  row.platformorder_set?.find(
                    (item: any) => item.platform === storeId
                  )?.api_code || "N/A"
                }
              />
            ),
            wrap: true,
            style: {minWidth, justifyContent: 'center'},
          };
        })
        .filter(Boolean)
    : [];

  const selectableRowSelected = useCallback(
    (row: any) => {
      if (!row?.id) return false
      return selectedOrderIdsSet.has(row.id)
    },
    [selectedOrderIdsSet]
  )

  const handleSelectedRowsChange = useCallback(
    ({selectedRows}: {selectedRows: any[]}) => {
      const pageIds = orderList?.map((r: any) => r?.id).filter(Boolean) || []
      const selectedOnPageIds = selectedRows?.map((r: any) => r?.id).filter(Boolean) || []

      const nextIds = (() => {
        const filtered = selectedOrderIds.filter((id) => !pageIds.includes(id))
        selectedOnPageIds.forEach((id) => {
          if (!filtered.includes(id)) {
            filtered.push(id)
          }
        })
        return filtered
      })()

      if (!arraysEqual(nextIds, selectedOrderIds)) {
        setSelectedOrderIds(nextIds)
      }

      setSelectedOrdersMap((prev) => {
        const next = {...prev}
        pageIds.forEach((id) => {
          if (!selectedOnPageIds.includes(id)) {
            delete next[id]
          }
        })
        selectedOnPageIds.forEach((id) => {
          const row = orderList?.find((r: any) => r?.id === id)
          if (row) {
            next[id] = row
          }
        })
        return next
      })
    },
    [orderList, selectedOrderIds]
  )

  const handleBulkSyncProceed = async () => {
    if (!selectedFailedOrders.length) {
      setShowBulkSyncModal(false)
      return
    }
    const failedOrderIds = selectedFailedOrders
      .filter((item) => item.status !== 'ONHOLD')
      .map((item) => item.id)
    const onHoldOrderIds = selectedFailedOrders
      .filter((item) => item.status === 'ONHOLD')
      .map((item) => item.id)
    const totalOrders = failedOrderIds.length + onHoldOrderIds.length
    setBulkSyncMeta({
      total: totalOrders,
      completed: 0,
      inProgress: true,
      success: false,
      message: '',
    })
    try {
      if (failedOrderIds.length) {
        const failedResponse = await dispatch(orderResyncAsync({failed_order_ids: failedOrderIds}))
        if (failedResponse.meta.requestStatus !== 'fulfilled') {
          throw new Error(failedResponse?.payload?.message || 'Failed to trigger order resync.')
        }
        setBulkSyncMeta((prev) => ({
          ...prev,
          completed: prev.completed + failedOrderIds.length,
        }))
      }
      if (onHoldOrderIds.length) {
        const holdResponse = await dispatch(onholdOrderResyncAsync({hold_order_ids: onHoldOrderIds}))
        if (holdResponse.meta.requestStatus !== 'fulfilled') {
          throw new Error(holdResponse?.payload?.message || 'Failed to trigger hold order resync.')
        }
        setBulkSyncMeta((prev) => ({
          ...prev,
          completed: prev.completed + onHoldOrderIds.length,
        }))
      }
      successToast('Bulk resync initiated for selected orders.')
      setBulkSyncMeta((prev) => ({
        ...prev,
        inProgress: false,
        success: true,
        message: 'All selected orders have been queued for sync.',
      }))
    } catch (error: any) {
      console.error('Bulk resync failed:', error)
      errorToast(error?.message || 'Bulk resync failed. Please try again.')
      setBulkSyncMeta((prev) => ({
        ...prev,
        inProgress: false,
        success: false,
        message: error?.message || 'Bulk resync failed. Please try again.',
      }))
    } finally {
      setShowBulkSyncModal(false)
      clearAllSelections()
      await dispatch(getOrdersListAsync({paramData: {...paginationData}, selectedPlatform: selectedPlatform}))
    }
  }

  const handleShippingLabels = async (
    action: 'print' | 'download',
    orders: any[] = selectedOrdersWithLabels
  ) => {
    if (orders.length === 0) {
      warningToast('No orders with valid shipping labels selected.')
      return
    }

    // Extract platform display names from selected orders and find matching IDs
    const platformDisplayNames = orders.map((order: any) => order?.platform?.display_name).filter(Boolean)
    const uniquePlatformNames = Array.from(new Set(platformDisplayNames))
    
    if (uniquePlatformNames.length === 0) {
      warningToast('Unable to determine platform for selected orders.')
      return
    }
    
    if (uniquePlatformNames.length > 1) {
      warningToast('Selected orders are from different channels. Please select orders from only one channel.')
      return
    }
    
    // Find the platform ID from fetchedConnectedPlatformData by matching display_name
    const platformName = uniquePlatformNames[0]
    const connectedPlatform = fetchedConnectedPlatformData.find(
      (item: any) => item?.display_name === platformName
    )
    
    if (!connectedPlatform?.id) {
      warningToast(`Unable to find platform ID for ${platformName}. Please try again.`)
      return
    }
    
    const platformIdToUse = String(connectedPlatform.id)

    // Get order IDs from selected orders
    const orderIds = orders.map((order: any) => order.id)

    // Show loading message based on action type
    const loadingMessage = action === 'print' ? 'Processing labels... please wait.' : 'Downloading labels... please wait.'
    toast.info(loadingMessage, {autoClose: false})

    const printFromHiddenIframe = (sourceUrl: string, onCleanup?: () => void) => {
      const iframe = document.createElement('iframe')
      iframe.style.position = 'fixed'
      iframe.style.right = '0'
      iframe.style.bottom = '0'
      iframe.style.width = '0'
      iframe.style.height = '0'
      iframe.style.border = '0'
      iframe.style.backgroundColor = '#ffffff'
      iframe.style.colorScheme = 'light'
      iframe.setAttribute('data-pdf-viewer', 'true')
      iframe.src = sourceUrl
      document.body.appendChild(iframe)

      let cleaned = false
      let focusCleanupTimer: number | undefined

      const onWindowFocus = () => {
        if (focusCleanupTimer) {
          window.clearTimeout(focusCleanupTimer)
        }
        // allow browser print preview to settle before removing iframe
        focusCleanupTimer = window.setTimeout(() => cleanup(), 500)
      }

      const cleanup = () => {
        if (cleaned) return
        cleaned = true
        window.removeEventListener('focus', onWindowFocus)
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe)
        }
        if (focusCleanupTimer) {
          window.clearTimeout(focusCleanupTimer)
        }
        onCleanup?.()
      }

      iframe.onload = () => {
        const printWindow = iframe.contentWindow
        if (!printWindow) {
          cleanup()
          return
        }

        try {
          printWindow.addEventListener('afterprint', cleanup, {once: true})
        } catch (error) {
          console.error('afterprint hook failed:', error)
        }

        window.addEventListener('focus', onWindowFocus, {once: true})

        try {
          printWindow.focus()
          printWindow.print()
        } catch (error) {
          console.error('Print failed:', error)
          cleanup()
          return
        }

        // hard fallback to prevent leaked iframes when browser events do not fire
        window.setTimeout(() => cleanup(), 60000)
      }
    }

    try {
      const response = await dispatch(
        downloadShippingLabelsAsync({
          platform_id: platformIdToUse,
          order_ids: orderIds,
        })
      ).unwrap()

      // Response is already an array of URLs
      const urls = response || []

      if (urls && urls.length > 0) {
        const handleIndividually = async () => {
          if (action === 'print') {
            urls.forEach((url: string) => printFromHiddenIframe(url))
            toast.dismiss()
            return
          }

          const {successCount, failedCount} = await downloadShippingLabelsIndividually(urls)
          toast.dismiss()

          if (successCount > 0) {
            successToast(`Downloaded ${successCount} shipping label(s).`)
          }
          if (failedCount > 0) {
            warningToast(`${failedCount} shipping label(s) could not be downloaded.`)
          }
        }

        try {
          const {pdfBytes, failedUrls} = await mergeShippingLabelUrls(urls)
          const blob = new Blob([new Uint8Array(pdfBytes).buffer], {type: 'application/pdf'})
          const mergedUrl = URL.createObjectURL(blob)

          if (action === 'print') {
            // Create hidden iframe for direct print dialog
            const iframe = document.createElement('iframe')
            iframe.style.position = 'fixed'
            iframe.style.right = '0'
            iframe.style.bottom = '0'
            iframe.style.width = '0'
            iframe.style.height = '0'
            iframe.style.border = '0'
            iframe.src = mergedUrl
            document.body.appendChild(iframe)

            iframe.onload = () => {
              try {
                iframe.contentWindow?.print()
              } catch (e) {
                console.error('Print failed:', e)
              }
              // Clean up after print dialog closes
              setTimeout(() => {
                document.body.removeChild(iframe)
                URL.revokeObjectURL(mergedUrl)
              }, 1000)
            }
            toast.dismiss()
            successToast('Opening print dialog for shipping labels...')
          } else {
            // Force download without opening in browser
            const link = document.createElement('a')
            link.href = mergedUrl
            link.download = `shipping-labels-${new Date().getTime()}.pdf`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            setTimeout(() => URL.revokeObjectURL(mergedUrl), 100)
            toast.dismiss()
            if (failedUrls.length > 0) {
              warningToast(`${failedUrls.length} label(s) could not be merged. Downloading them separately.`)
              await downloadShippingLabelsIndividually(failedUrls)
              successToast('Merged shipping labels downloaded successfully.')
            } else {
              successToast('Merged shipping labels downloaded successfully.')
            }
          }

          clearAllSelections()
        } catch (mergeError) {
          console.error('Failed to merge labels. Falling back to individual files.', mergeError)
          warningToast(`Could not merge labels. ${action === 'print' ? 'Printing' : 'Downloading'} separately.`)
          await handleIndividually()
          clearAllSelections()
        }
      } else {
        toast.dismiss()
        warningToast('No shipping labels found for the selected orders.')
      }
    } catch (error: any) {
      console.error('Error fetching shipping labels:', error)
      toast.dismiss()
      errorToast(error?.message || 'Failed to fetch shipping labels. Please try again.')
    }
  }

  const handleRequestNewShippingLabel = async () => {
    const orderIds = selectedOrdersForNewLabel
      .map((order: any) => Number(order.id))
      .filter((id: number) => Number.isFinite(id))

    if (!orderIds.length) {
      warningToast('Please select orders with N/A or FAILED shipping label status.')
      return
    }

    setRequestShippingLabelLoading(true)
    try {
      const response: any = await dispatch(
        requestNewShippingLabelAsync({
          order_ids: orderIds,
        })
      )

      if (response?.meta?.requestStatus !== 'fulfilled') {
        throw new Error(
          response?.payload?.data?.message ||
            response?.payload?.message ||
            'Failed to request new shipping label.'
        )
      }

      successToast(
        response?.payload?.massage ||
          response?.payload?.message ||
          'Shipping label request submitted successfully.'
      )

      clearAllSelections()
      dispatch(getOrdersListAsync({paramData: {...paginationData}, selectedPlatform}))
    } catch (error: any) {
      console.error('Request New Shipping Label failed:', error)
      errorToast(error?.message || 'Failed to request new shipping label.')
    } finally {
      setRequestShippingLabelLoading(false)
    }
  }

  const handleOrderDocumentDownload = async (type: OrderPackingSlipDownloadType) => {
    const orderIds = filterOrdersForDocumentDownloadByType(
      Object.values(selectedOrdersMap),
      type
    )
      .map((order: any) => Number(order.id))
      .filter((id: number) => Number.isFinite(id))

    await runOrderDocumentDownload({
      dispatch,
      orderIds,
      type,
      clearAllSelections,
      successToast,
      warningToast,
      errorToast,
    })
  }

  const handleDownloadInvoice = async () => {
    const orderIds = selectedInvoiceOrders
      .map((order: any) => Number(order.id))
      .filter((id: number) => Number.isFinite(id))

    if (!orderIds.length) {
      warningToast('Please select shipped orders to download invoice.')
      return
    }

    setInvoiceDownloadLoading(true)
    const downloadingToastId =
      orderIds.length > 1
        ? toast.info('Invoices are downloading. This may take a moment...', {autoClose: false})
        : null
    try {
      const response: any = await dispatch(downloadOrderInvoiceAsync({order_ids: orderIds}))
      const payload = response?.payload
      const responseData = payload?.data && typeof payload.data === 'object' ? payload.data : payload
      const apiStatusCode = Number(responseData?.status_code ?? payload?.status)

      if (
        response?.meta?.requestStatus !== 'fulfilled' ||
        payload?.success !== true ||
        (Number.isFinite(apiStatusCode) && (apiStatusCode < 200 || apiStatusCode >= 300))
      ) {
        throw new Error(
          responseData?.message ||
            responseData?.massage ||
            responseData?.detail ||
            payload?.message ||
            payload?.massage ||
            payload?.detail ||
            'Failed to download invoice.'
        )
      }

      successToast(
        payload?.message ||
          (orderIds.length > 1
            ? `Invoices downloaded successfully (${orderIds.length}).`
            : 'Invoice downloaded successfully.')
      )
      clearAllSelections()
    } catch (error: any) {
      console.error('Invoice download failed:', error)
      errorToast(error?.message || 'Failed to download invoice.')
    } finally {
      if (downloadingToastId) {
        toast.dismiss(downloadingToastId)
      }
      setInvoiceDownloadLoading(false)
    }
  }

  const handleDownloadRowInvoice = async (row: any) => {
    const orderId = Number(row?.id)
    if (!Number.isFinite(orderId) || !isInvoiceEligibleOrder(row)) {
      warningToast('Invalid selected order.')
      return
    }

    setInvoiceDownloadLoading(true)
    try {
      const response: any = await dispatch(downloadOrderInvoiceAsync({order_id: orderId}))
      const payload = response?.payload
      const responseData = payload?.data && typeof payload.data === 'object' ? payload.data : payload
      const apiStatusCode = Number(responseData?.status_code ?? payload?.status)

      if (
        response?.meta?.requestStatus !== 'fulfilled' ||
        payload?.success !== true ||
        (Number.isFinite(apiStatusCode) && (apiStatusCode < 200 || apiStatusCode >= 300))
      ) {
        throw new Error(
          responseData?.message ||
            responseData?.massage ||
            responseData?.detail ||
            payload?.message ||
            payload?.massage ||
            payload?.detail ||
            'Failed to download invoice.'
        )
      }

      successToast(payload?.message || 'Invoice downloaded successfully.')
    } catch (error: any) {
      console.error('Invoice download failed:', error)
      errorToast(error?.message || 'Failed to download invoice.')
    } finally {
      setInvoiceDownloadLoading(false)
    }
  }

// Inside your component
  const columns:any = [
  //   {
  //   name: 'S.No',
  //   cell: (row, index) => (paginationData.page - 1) * paginationData.limit + index + 1,    width: '65px',
  //   center: true,
  // },

   {
  name: (
    <span className="d-inline-flex align-items-center gap-2">
      Channel Order #
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip id="channel-order-tooltip">The unique identification number assigned to the order by the original sales marketplace/Platform</Tooltip>}
      >
        <span style={{ cursor: 'pointer', color: 'darkgrey' }}>
          <FontAwesomeIcon icon={faInfoCircle} />
        </span>
      </OverlayTrigger>
    </span>
  ),
  cell: (row) => (row ? <ColProductName rowData={row} omsPlatformId={omsPlatformId} /> : null),
  sortable: true,
  sortField: 'api_code',
  style: {overflow: 'hidden', minWidth: '150px'},
},
    {
      name: (
        <>
          <div style={{display: "flex", alignItems:"center", justifyContent: "center", gap: "6px"}}>
            Order Create Date{' '}
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>The exact date and time the customer placed the order on the external sales channel. Showing in your Timezone.</Tooltip>}
              >
              <span style={{ cursor: 'pointer', color: 'darkgrey' }}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </span>
            </OverlayTrigger>
            </div>
        </>
      ),
      cell: (row) => {
        return (
              <CommonColumn
                rowData={
                  row?.api_created_at
                    ? formatDateLocal(row?.api_created_at)
                    : 'N/A'
                }
              />
            
        );
      },
      width: '160px',
      style: {minWidth: '160px', justifyContent: 'flex-end'},
      sortField: 'api_created_at',
    },
     {
      name: (
        <>
          <div style={{display: "flex", alignItems:"center", justifyContent: "center", gap: "6px"}}>
            Import Date{' '}
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>The timestamp indicating when Commercium successfully fetched and recorded the order data from the connected channel. Showing in your Timezone.</Tooltip>}
              >
              <span style={{ cursor: 'pointer', color: 'darkgrey' }}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </span>
            </OverlayTrigger>
            </div>
        </>
      ),
      cell: (row) => {
        return (
              <CommonColumn
                rowData={
                  row?.created_at
                    ? formatDateLocal(row?.created_at)
                    : 'N/A'
                }
              />
            
        );
      },
      width: '160px',
      style: {minWidth: '160px', justifyContent: 'flex-end'},
      sortField: 'created_at',
    },
    ...dynamicStatusColumns,
    ...(cancel_status_available ? [{
      name: (
        <div style={filterableHeaderStyle}>
          <span style={filterableHeaderLabelStyle}>Source Cancel Status</span>
          <span style={filterableHeaderActionsStyle}>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Tracks cancellation synchronization. N/A means no request of cancellation, Synced means successful update, and Failed indicates a sync error occurred</Tooltip>}
            >
              <span
                style={{ cursor: 'pointer', color: 'darkgrey' }}
                onClick={(e) => e.stopPropagation()}
              >
                <FontAwesomeIcon icon={faInfoCircle} />
              </span>
            </OverlayTrigger>
            <FilterDropdown
              filters={filters}
              setFilters={setFilters}
              hasActiveFilter={paginationData.status_filters?.cancel_status !== undefined}
              options={[
                {
                  label: 'Filter Status',
                  key: 'cancel_status',
                  values: [
                    {value: 'Cancelled', label: 'Cancelled'},
                    {value: 'Not Cancelled', label: 'Not Cancelled'},
                    {value: 'NA', label: 'N/A'},
                  ],
                },
              ]}
            />
          </span>
        </div>
      ),
      cell: (row) => {
        const primaryOrderCancelled = row?.platformorder_set?.find(
          (item: any) => item?.platform === mainStoreData?.id
        )?.isCancelled;
        const isCancelled = primaryOrderCancelled ?? row?.isCancelled;
        
        if (isCancelled) {
          return (
            <div className="d-flex align-items-center w-100 justify-content-center">
              <span className='badge rounded-pill'
                style={{
                  backgroundColor: 'red',
                  color: 'white',
                  padding: '4px 10px',
                  border: 'none',
                  cursor: 'pointer',
                  minWidth: '100px',
                  fontWeight: 500,
                  fontSize: '11px'
                }}
              >
                Cancelled
              </span>
            </div>
          );
        }
        
        return (
          <div className="d-flex align-items-center w-100 justify-content-center">
            <CommonColumn rowData="N/A" />
          </div>
        );
      },
      wrap: true,
      minWidth: '190px',
      style: {justifyContent: 'center'},
      sortField: 'is_cancelled',
    }] : []),
    {
      name: (
    <span className="d-inline-flex align-items-center gap-2">
      Channel
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip id="channel-order-tooltip"> The specific store or marketplace integration where the order was captured.</Tooltip>}
      >
        <span style={{ cursor: 'pointer', color: 'darkgrey' }}>
          <FontAwesomeIcon icon={faInfoCircle} />
        </span>
      </OverlayTrigger>
    </span>
  ),
      cell: (row) =>
        row ? (
          <div className='d-flex justify-content-center align-items-center'>
            <img
              src={row.platform.platform_info.logo_ref}
              alt='Channel Logo'
              className='me-1'
              style={{width: '20px', height: '20px'}}
            />
            <span>{row.platform.display_name}</span>
          </div>
        ) : null,
      width: '140px',
      style: {minWidth: '140px', justifyContent: 'center'},
      // style: {
      //   // width: '30px', // Adjust the width as needed
      //   overflow: 'hidden',
      //   textOverflow: 'ellipsis',

      // },
    },
    ...channelOrderNumberColumns,
    {
      name: 'Action',
      cell: (row: any) => (
        <OrderRowActionsCell
          row={row}
          omsPlatformId={omsPlatformId}
          invoiceDownloadLoading={invoiceDownloadLoading}
          shouldShowPackageAction={shouldShowPackageAction}
          isInvoiceEligibleOrder={isInvoiceEligibleOrder}
          onDownloadInvoice={handleDownloadRowInvoice}
          onOpenPackageModal={handleOpenPackageModal}
          onEditOrder={(effectiveOrderId) => navigate(AppUrls.editOmsOrder(effectiveOrderId))}
          onOpenOmsPackageModal={handleOpenOmsPackageModal}
          onOpenDirectShipmentModal={handleOpenDirectShipmentModal}
        />
      ),
      center: true,
      width: '120px',
      ignoreRowClick: true,
      button: true,
      style: {justifyContent: 'center'},
    },
    // {
    //   name: 'Buyer',
    //   cell: (row) => {
    //     const customerName = row?.customer?.name;
    //     return customerName ? <CommonColumn rowData={customerName} /> : "N/A";
    //   },
    //   sortable: true,
    //   center: true,
    // },
    // {
    //   name: 'Total Price',
    //   cell: (row) => row? (
    //     <CommonColumn rowData={row?.total_price?.value} />
    //   ) :null,
    //   sortable: true,
    //   center: true,
    // },
  ].filter(Boolean)


  const handleSort = (ev, sortDirection) => {
    setPaginationData((pre) => {
      return {...pre, sort_by: ev.sortField, sort_order: (sortDirection + '').toUpperCase()}
    })
  }
 
  // useEffect(() => {
  //   setPaginationData((pre) => {
  //     return {
  //       ...pre,
  //       search_string: searchString,
  //       page: 1,
  //       sync_status: selectedSyncStatus, // Include selectedSyncStatus here
  //       shipment_status: selectedShipmentStatus,
  //       // api_created_at: selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : '',
  //     }
  //   })
  // }, [selectedSyncStatus, selectedShipmentStatus])

  useEffect(() => {
    if (connectedPlatformTypesLoading) return;
    dispatch(
      getOrdersListAsync({paramData: {...paginationData}, selectedPlatform: selectedPlatform})
    )
  }, [paginationData, selectedPlatform, connectedPlatformTypesLoading])

  return (
    <>
      <div className='mb-3 d-flex justify-content-between align-items-center'>
        <span className='fw-bold text-lg ' style={{fontSize: '16px'}}>
          Sales Order
        </span>

      </div>
      <div className='card card-flush'>
        <div className='card-header border-0 px-3 py-3 d-flex flex-wrap align-items-center justify-content-between gap-2'>
          <div className='card-title m-0'>
            <div className='d-flex flex-wrap align-items-center gap-2'>
              <input
                className='form-control form-control-sm'
                type='search'
                placeholder='Search Order ID, Customer...'
                aria-label='Search'
                style={{width: '220px', height: '32px', fontSize: '13px'}}
                onChange={(ev) => {
                  setSearchString(ev.target.value)
                }}
                onKeyDown={(ev) => {
                  if (ev.key == 'Enter') {
                    setPaginationData((pre) => {
                      return {...pre, search_string: searchString, page: 1}
                    })
                  }
                }}
                value={searchString}
              />
              <select
                className='form-select form-select-sm'
                aria-label='Select platform'
                style={{width: '130px', height: '32px', fontSize: '13px'}}
                value={selectedPlatform}
                onChange={(ev) => {
                  setSelectedPlatform(ev.target.value)
                }}
              >
                <option value={''}>All Channels</option>
                {fetchedConnectedPlatformData.length &&
                  fetchedConnectedPlatformData?.map((item) => {
                    if (item?.isMainStore == false && item?.isAccountingStore == false && item?.isShippingStore == false) {
                      return (
                        <option key={item?.id + 'platform'} value={item?.id}>
                          {item?.display_name}
                        </option>
                      )
                    }
                  })}
              </select>
              <div className='position-relative'>
                <button
                  className='btn btn-sm bg-white border text-dark d-inline-flex align-items-center justify-content-center text-nowrap'
                  style={{width: '160px', height: '32px', fontSize: '13px', padding: '0 0.5rem'}}
                  onClick={() => setShowDateRangePicker(!showDateRangePicker)}
                >
                  {selectedStarDate && selectedEndDate
                    ? `${moment(selectedStarDate).format('YYYY/MM/DD')}  -  ${moment(selectedEndDate).format('YYYY/MM/DD')}`
                    : 'Pick a Date Range'}
                </button>
                <AppDatePickerRange
                  isVisible={showDateRangePicker}
                  handleClose={() => setShowDateRangePicker(false)}
                  onPickRange={({startDate, endDate}) => {
                    setSelectedStartDate(startDate)
                    setSelectedEndDate(endDate)
                    setPaginationData((pre) => ({
                      ...pre,
                      api_created_at_min: moment(startDate).format('YYYY-MM-DD'),
                      api_created_at_max: moment(endDate).format('YYYY-MM-DD'),
                      page: 1,
                    }))
                  }}
                />
              </div>
              <button
                className='btn btn-sm btn-light border fw-semibold text-nowrap d-inline-flex align-items-center justify-content-center'
                style={{height: '32px', fontSize: '13px', padding: '0 0.75rem'}}
                onClick={handleResetFilters}
              >
                Reset
              </button>
            </div>
          </div>
          <div className='card-toolbar m-0'>
            <div className='d-flex align-items-center gap-2 flex-wrap justify-content-md-end'>
              {(isWrite && !isRestrictedAccess) && (
                <Dropdown>
                  <Dropdown.Toggle
                    variant='primary'
                    size='sm'
                    className='fw-bold d-inline-flex align-items-center justify-content-center text-nowrap'
                    disabled={isReadOnlyAccess}
                    style={{fontSize: '13px', padding: '0 0.65rem', height: '32px'}}
                  >
                    Bulk Actions ({selectedOrderIds.length} Selected)
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={() => setShowBulkSyncModal(true)}
                      disabled={selectedFailedOrders.length === 0 || bulkSyncMeta.inProgress}
                    >
                      Resync Selected Failed Orders
                      {selectedFailedOrders.length > 0 && ` (${selectedFailedOrders.length})`}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={handleDownloadInvoice}
                      disabled={selectedInvoiceOrders.length === 0 || invoiceDownloadLoading}
                    >
                      {invoiceDownloadLoading ? 'Downloading Invoice...' : 'Download Invoice'}
                      {selectedInvoiceOrders.length > 0 && ` (${selectedInvoiceOrders.length})`}
                    </Dropdown.Item>
                    {(showOrderDocumentDownloadActions ||
                      showAmazonVendorLabelDocumentDownloadActions) && (
                      <>
                        {showOrderDocumentDownloadActions && (
                          <Dropdown.Item
                            onClick={() => handleOrderDocumentDownload('PACKING_SLIP')}
                            disabled={
                              selectedOrdersForDocumentDownload.length === 0 ||
                              orderDocumentsDownloadLoading
                            }
                          >
                            Download Packing Slip(s)
                            {selectedOrdersForDocumentDownload.length > 0 &&
                              ` (${selectedOrdersForDocumentDownload.length})`}
                          </Dropdown.Item>
                        )}
                        {showAmazonVendorLabelDocumentDownloadActions && (
                          <>
                            <Dropdown.Item
                              onClick={() => handleOrderDocumentDownload('SHIPPING_LABEL')}
                              disabled={
                                selectedOrdersForAmazonVendorDocumentDownload.length === 0 ||
                                orderDocumentsDownloadLoading
                              }
                            >
                              Download Shipping Label(s)
                              {selectedOrdersForAmazonVendorDocumentDownload.length > 0 &&
                                ` (${selectedOrdersForAmazonVendorDocumentDownload.length})`}
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => handleOrderDocumentDownload('BOTH')}
                              disabled={
                                selectedOrdersForAmazonVendorDocumentDownload.length === 0 ||
                                orderDocumentsDownloadLoading
                              }
                            >
                              Download Packing Slip + Shipping Label
                              {selectedOrdersForAmazonVendorDocumentDownload.length > 0 &&
                                ` (${selectedOrdersForAmazonVendorDocumentDownload.length})`}
                            </Dropdown.Item>
                          </>
                        )}
                      </>
                    )}
                    {showLegacyShippingLabelActions && (
                      <>
                        <Dropdown.Item
                          onClick={() => handleShippingLabels('print', selectedLegacyOrdersWithLabels)}
                          disabled={selectedLegacyOrdersWithLabels.length === 0 || shippingLabelsLoading}
                        >
                          Print Shipping Labels
                          {selectedLegacyOrdersWithLabels.length > 0 &&
                            ` (${selectedLegacyOrdersWithLabels.length})`}
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() =>
                            handleShippingLabels('download', selectedLegacyOrdersWithLabels)
                          }
                          disabled={selectedLegacyOrdersWithLabels.length === 0 || shippingLabelsLoading}
                        >
                          Download Shipping Labels
                          {selectedLegacyOrdersWithLabels.length > 0 &&
                            ` (${selectedLegacyOrdersWithLabels.length})`}
                        </Dropdown.Item>
                        {showRequestNewShippingLabelAction && (
                          <Dropdown.Item
                            onClick={handleRequestNewShippingLabel}
                            disabled={selectedOrdersForNewLabel.length === 0 || requestShippingLabelLoading}
                          >
                            Request New Shipping Label
                            {selectedOrdersForNewLabel.length > 0 && ` (${selectedOrdersForNewLabel.length})`}
                          </Dropdown.Item>
                        )}
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              )}
              {(isWrite && !isRestrictedAccess && !!omsPlatformId) && (
                <>
                  <button
                    type='button'
                    className='btn btn-sm btn-primary fw-bold text-nowrap d-inline-flex align-items-center justify-content-center'
                    disabled={isReadOnlyAccess}
                    onClick={() => navigate(AppUrls.createOmsOrder())}
                    style={{fontSize: '13px', padding: '0 0.65rem', height: '32px'}}
                  >
                    + Create Order
                  </button>
                  <Dropdown align='end' className='d-inline-block'>
                    <Dropdown.Toggle
                      variant='light'
                      className='d-inline-flex align-items-center justify-content-center'
                      style={{
                        height: '32px',
                        width: '32px',
                        padding: 0,
                        backgroundColor: '#f5f8fa',
                        border: '1px solid #e4e6ef',
                        borderRadius: '6px',
                        boxShadow: 'none',
                      }}
                      bsPrefix='btn'
                    >
                      <svg width='16' height='16' viewBox='0 0 24 24' fill='#555' aria-hidden='true'>
                        <circle cx='12' cy='5' r='2' />
                        <circle cx='12' cy='12' r='2' />
                        <circle cx='12' cy='19' r='2' />
                      </svg>
                    </Dropdown.Toggle>
                    <Dropdown.Menu
                      style={{
                        fontSize: '14px',
                        minWidth: '240px',
                        padding: '8px 0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
                      }}
                    >
                      <Dropdown.Item
                        onClick={() => setShowUploadOrdersCsvModal(true)}
                        disabled={isReadOnlyAccess}
                        className='d-flex align-items-center gap-2 py-2 px-3'
                      >
                        <span
                          className='d-inline-flex align-items-center justify-content-center'
                          style={{width: 22, fontSize: '16px'}}
                          aria-hidden='true'
                        >
                          📥
                        </span>
                        <span>Bulk Import Orders</span>
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => navigate('/custom-fields')}
                        className='d-flex align-items-center gap-2 py-2 px-3'
                      >
                        <span
                          className='d-inline-flex align-items-center justify-content-center'
                          style={{width: 22, fontSize: '16px', color: '#a78bfa'}}
                          aria-hidden='true'
                        >
                          <FontAwesomeIcon icon={faCog} />
                        </span>
                        <span>Manage Custom Fields</span>
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </>
              )}
            </div>
          </div>
        </div>
        <TableLoadingShell loading={connectedPlatformTypesLoading || orderListLoading}>
        <DataTable
          fixedHeader={true}
          data={orderList}
          columns={columns}
          noDataComponent={<div className='text-secondary'>
                          <h5>No records found</h5>
                         </div>}
          persistTableHead
          selectableRows
          selectableRowsHighlight
          selectableRowSelected={selectableRowSelected}
          keyField='id'
          selectableRowsComponent={TableCheckbox as any}
          clearSelectedRows={clearSelectedRowsFlag}
          onSelectedRowsChange={handleSelectedRowsChange}
          onSort={handleSort}
          responsive={true}
          paginationTotalRows={orderPaginationData.count}
          pagination
          paginationServer
          paginationRowsPerPageOptions={[10, 20, 30, 100]}
          progressPending={false}
          paginationServerOptions={{
            persistSelectedOnPageChange: false,
            persistSelectedOnSort: true,
          }}
          onChangePage={(pageNumber, totalRows) => {
            setPaginationData((pre) => {
              return {...pre, page: pageNumber}
            })
          }}
          onChangeRowsPerPage={(newLimit, newPage) => {
            setPaginationData((pre) => {
              return {...pre, limit: newLimit, page: newPage || 1}
            })
          }}
          customStyles={{
            table: {
              style: {
                height: '55vh',
              },
            },
            headCells: {
              style: {
                alignItems: 'flex-start',
                fontWeight: 'bold',
                fontSize: '14px',
                lineHeight: 1.25,
                minHeight: '74px',
                overflow: 'visible',
                padding: '8px 10px',
                whiteSpace: 'normal',
              },
            },
            cells: {
              style: {
                padding: '10px',
                borderBottom: '1px solid gainsboro',
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'unset',
                wordBreak: 'break-word',
              },
            },
          }}
        />
        </TableLoadingShell>
        {bulkSyncMeta.total > 0 && (
          <div className='px-3 pb-3'>
            <div className='d-flex align-items-center justify-content-between mb-2 gap-3 flex-wrap'>
              <span className='fw-semibold'>
                Syncing failed orders ({bulkSyncMeta.completed}/{bulkSyncMeta.total} complete)
              </span>
              {bulkSyncMeta.inProgress && <Spinner animation='border' size='sm' />}
            </div>
            <ProgressBar
              now={bulkSyncProgress}
              animated={bulkSyncMeta.inProgress}
              striped={bulkSyncMeta.inProgress}
            />
            {bulkSyncMeta.message && (
              <div
                className={`small mt-2 ${bulkSyncMeta.success ? 'text-success' : 'text-danger'}`}
              >
                {bulkSyncMeta.message}
              </div>
            )}
          </div>
        )}
      </div>
      <Modal centered show={showBulkSyncModal} onHide={() => setShowBulkSyncModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Sync Selected Failed Orders?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='mb-0'>
            This will attempt to re-sync {selectedFailedOrders.length} order(s) that are currently
            marked as failed or on hold.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            onClick={() => setShowBulkSyncModal(false)}
            disabled={bulkSyncMeta.inProgress}
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={handleBulkSyncProceed}
            disabled={bulkSyncMeta.inProgress || !selectedFailedOrders.length}
          >
            Proceed
          </Button>
        </Modal.Footer>
      </Modal>
      <UploadCsvOrdersModal
        show={showUploadOrdersCsvModal}
        handleClose={() => setShowUploadOrdersCsvModal(false)}
        platformId={omsPlatformId ? String(omsPlatformId) : undefined}
      />
      <ManagePackagesModal
        show={showOmsPackageModal}
        onClose={handleCloseOmsPackageModal}
        orderId={selectedOmsPackageOrder?.id || null}
        orderCode={selectedOmsPackageOrder?.api_code || selectedOmsPackageOrder?.api_id || ''}
        onSwitchRoute={() => {
          handleCloseOmsPackageModal()
          setSelectedDirectShipmentOrder(selectedOmsPackageOrder)
          setShowDirectShipmentModal(true)
        }}
      />
      <ManageShipmentsModal
        show={showDirectShipmentModal}
        onClose={handleCloseDirectShipmentModal}
        orderId={selectedDirectShipmentOrder?.id || null}
        orderCode={selectedDirectShipmentOrder?.api_code || selectedDirectShipmentOrder?.api_id || ''}
        onSwitchRoute={() => {
          handleCloseDirectShipmentModal()
          setSelectedOmsPackageOrder(selectedDirectShipmentOrder)
          setShowOmsPackageModal(true)
        }}
      />
      <OrderPackageModal
        show={showPackageModal}
        loading={packageModalLoading}
        saving={packageSaving}
        mode={packageModalMode}
        order={selectedPackageOrder}
        lineItems={packageLineItems}
        form={packageForm}
        onFormChange={setPackageForm}
        onClose={handleClosePackageModal}
        onSave={handleSavePackage}
      />
    </>
  )
}

export default Orders
